// "What should this field say?" — binding a text field to record data.
//
// CONSUMER: internal-ui.
//
// Grant, 2026-08-22: "those are going to be a lot of fields, so make
// sure the UI and UX is easy to work with as it pertains to that."
//
// The catalog is large by design, so this is built for scale rather than
// being a dropdown that grows until it's unusable:
//
//   * SEARCH FIRST — the box is focused on open, and typing filters.
//     "purch" finds Purchase price without anyone scrolling.
//   * EVERY OPTION SHOWS THIS RECORD'S ACTUAL VALUE. This is the real
//     answer to "a lot of fields": you pick by recognizing the right
//     answer ("Purchase price · $450,000"), never by guessing which
//     field name holds it.
//   * GROUPED, RECORD FIRST, named with the record's own product noun
//     ("This Refinance Mortgage"), not a schema word.
//   * NO DEAD ENTRIES — the server only returns fields this record type
//     can fill.
//   * A field with NO VALUE on this record is shown and marked, not
//     hidden: it is bindable, and the send-time guard is what stops a
//     blank going out. Hiding it would leave the advisor wondering
//     where the field went.

import { useEffect, useMemo, useRef, useState } from "react";

import { Button } from "../../ui";

export interface MergeFieldOption {
  token: string;
  label: string;
  group: string;
  group_label: string;
  value: string | null;
  sample: string;
}

export interface MergeFieldPickerProps {
  /** Null while still loading. The PARENT owns the fetch: the prep
   *  screen needs these same values to render a bound field's live text
   *  on the page, so fetching them here too would ask the server the
   *  same question twice. */
  options: MergeFieldOption[] | null;
  /** Plain English when the load failed — binding is unavailable, but
   *  typing a value by hand never is. */
  loadError: string | null;
  /** The token currently bound, so it can be shown as selected. */
  selected: string | null;
  onPick: (token: string, value: string | null) => void;
  onUnbind: () => void;
  onClose: () => void;
}

export function MergeFieldPicker({
  options,
  loadError,
  selected,
  onPick,
  onUnbind,
  onClose,
}: MergeFieldPickerProps) {
  const [query, setQuery] = useState("");
  const searchRef = useRef<HTMLInputElement | null>(null);

  // Focused on open: typing is the primary interaction, so the caret
  // belongs in the search box without anyone clicking there first.
  useEffect(() => {
    searchRef.current?.focus();
  }, [options]);

  const groups = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matching = (options ?? []).filter((o) => {
      if (!needle) return true;
      // Matches the LABEL and the VALUE, not the token: an advisor
      // searching "450" is looking for the field that holds $450,000,
      // and searching "address" shouldn't require knowing it lives
      // under service_record.
      return (
        o.label.toLowerCase().includes(needle) ||
        (o.value ?? "").toLowerCase().includes(needle) ||
        o.group_label.toLowerCase().includes(needle)
      );
    });
    const byGroup = new Map<string, MergeFieldOption[]>();
    for (const o of matching) {
      const list = byGroup.get(o.group_label) ?? [];
      list.push(o);
      byGroup.set(o.group_label, list);
    }
    // Record first — it is what the document is about — then the people.
    return [...byGroup.entries()].sort((a, b) => {
      const aRecord = a[0].startsWith("This ") ? 0 : 1;
      const bRecord = b[0].startsWith("This ") ? 0 : 1;
      return aRecord - bRecord || a[0].localeCompare(b[0]);
    });
  }, [options, query]);

  return (
    <div className="esign-merge-picker">
      <div className="esign-merge-picker__head">
        <input
          ref={searchRef}
          className="esign-merge-picker__search"
          type="text"
          value={query}
          placeholder="Search fields…"
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") onClose();
          }}
        />
      </div>

      {loadError ? <p className="esign-prep-hint">{loadError}</p> : null}

      {options === null ? (
        <p className="esign-prep-hint">Loading this record's fields…</p>
      ) : groups.length === 0 ? (
        <p className="esign-prep-hint">
          {query.trim()
            ? `Nothing matches "${query.trim()}".`
            : "This record has no fields that can be merged yet."}
        </p>
      ) : (
        <div className="esign-merge-picker__list">
          {groups.map(([groupLabel, entries]) => (
            <div key={groupLabel} className="esign-merge-picker__group">
              <span className="esign-prep-eyebrow">{groupLabel}</span>
              {entries.map((o) => (
                <button
                  key={o.token}
                  type="button"
                  className="esign-merge-picker__option"
                  data-selected={o.token === selected ? "true" : undefined}
                  onClick={() => onPick(o.token, o.value)}
                >
                  <span className="esign-merge-picker__label">{o.label}</span>
                  {/* The value is the point — it is how someone picks by
                      recognition rather than by guessing a field name. */}
                  {/* "Signer's name" has no single value to show: it is
                      a different name in each box (Baker, 2026-10-03),
                      so it says that rather than "nothing on this
                      record", which would read as broken. */}
                  <span
                    className="esign-merge-picker__value"
                    data-empty={o.value || o.group === "signer" ? undefined : "true"}
                  >
                    {o.value ?? (o.group === "signer" ? "each signer's own name" : "nothing on this record yet")}
                  </span>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}

      {selected ? (
        <div className="esign-merge-picker__foot">
          <Button variant="danger-ghost" size="sm" onClick={onUnbind}>
            Unbind — make it plain text
          </Button>
        </div>
      ) : null}
    </div>
  );
}
